import { useMemo, useState } from "react";

import Drawer from "../../../components/Drawer/Drawer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../../components/ui/accordion";
import { splitJobsByStatus } from "../../../handlers/jobStatus";
import JobListingItem, { JobListingItemJob } from "../../JobsPage/components/JobListingItem";
import ViewJob from "../../StaticPages/HomePage/components/ViewJob";

type CompanyJob = JobListingItemJob & {
	jobStatus?: string;
};

interface CompanyJobsProps {
	jobs: CompanyJob[];
	logo?: string;
}

/** Shorter than the in-drawer banner wording, which this CTA front-runs, to suit a list row. */
const ROLE_EXPLAINER_CTA_LABEL = "Explain this role";

function CompanyJobs({ jobs, logo }: CompanyJobsProps) {
	const [showJobDetailsModal, setShowJobDetailsModal] = useState(false);
	const [selectedJob, setSelectedJob] = useState(null);
	const [autoRunRoleExplainer, setAutoRunRoleExplainer] = useState(false);

	const { openJobs, closedJobs } = useMemo(() => splitJobsByStatus(jobs ?? []), [jobs]);

	/**
	 * Open a job in the drawer.
	 * `explain` starts the Role Explainer straight away, so it is only set by the CTA that
	 * promises one — a closed role, or the job title link, still opens plain job details.
	 */
	const viewJob = (job: CompanyJob, explain = false) => {
		setSelectedJob(job);
		setAutoRunRoleExplainer(explain);
		setShowJobDetailsModal(true);
	};

	return (
		<>
			<Drawer
				isOpen={showJobDetailsModal}
				onClose={() => {
					setShowJobDetailsModal(false);
				}}
			>
				<ViewJob
					data={selectedJob}
					setData={setSelectedJob}
					setShowJobDetailsModal={setShowJobDetailsModal}
					autoRunRoleExplainer={autoRunRoleExplainer}
				/>
			</Drawer>
			<div className="mt-4">
				<div className="pt-6">
					<h2 className="text-2xl font-semibold mb-4 font-plus-jakarta">
						{openJobs.length === 0 ? "No Open Positions Available" : "Open Positions"}
					</h2>
					<div className="">
						{openJobs.map((job) => (
							<JobListingItem
								key={job._id}
								job={{
									...job,
									logo: logo,
								}}
								showCompany={false}
								onClick={() => viewJob(job)}
								// The CTA is the Role Explainer entry point on this page: it opens the
								// drawer with the explanation already running.
								onApply={() => viewJob(job, true)}
								ctaLabel={ROLE_EXPLAINER_CTA_LABEL}
								titleClassName="text-sm font-semibold"
							/>
						))}
					</div>
				</div>

				{closedJobs.length > 0 && (
					// Collapsed by default: closed roles are secondary to the open ones above.
					<Accordion type="single" collapsible className="pt-8">
						<AccordionItem value="recently-closed" className="border-b-0">
							<AccordionTrigger className="px-0 py-0 hover:no-underline">
								<h2 className="text-2xl font-semibold font-plus-jakarta">
									Recently Closed
									<span className="ml-2 font-normal text-gray-500">({closedJobs.length})</span>
								</h2>
							</AccordionTrigger>
							<AccordionContent>
								<p className="text-sm text-gray-600 mb-2 pt-1">These roles are no longer accepting applications.</p>
								{closedJobs.map((job) => (
									<JobListingItem
										key={job._id}
										job={{
											...job,
											logo: logo,
										}}
										showCompany={false}
										onClick={() => viewJob(job)}
										onApply={() => viewJob(job)}
										titleClassName="text-sm font-semibold"
										// Closed roles are read-only: no saving, just the job details.
										hideSaveButton
										ctaLabel="View"
										ctaVariant="secondary"
										statusBadge="Closed"
									/>
								))}
							</AccordionContent>
						</AccordionItem>
					</Accordion>
				)}
			</div>
		</>
	);
}

export default CompanyJobs;
